import React, { useCallback, useId, useState } from 'react';
import { Box, Popover } from '@mui/material';
import { useTranslation } from 'react-i18next';

type Lang = 'ru' | 'en';

const LANGS: Lang[] = ['ru', 'en'];

function flagEmoji(code: Lang): string {
  return code === 'ru' ? '🇷🇺' : '🇺🇸';
}

/** Кнопка 64×40 + Popover — в одном стиле с ThemeSelector. */
const LanguageSwitcher: React.FC<{ inverted?: boolean }> = ({ inverted = false }) => {
  const { i18n, t } = useTranslation();
  const listId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const lng: Lang = i18n.language.startsWith('en') ? 'en' : 'ru';

  const menuBg = inverted ? '#000000' : '#eceff4';
  const flagBorder = inverted ? '#fff' : 'rgba(13,13,13,0.35)';
  const flagBorderMuted = inverted ? 'rgba(255,255,255,0.45)' : 'rgba(13,13,13,0.22)';

  const close = useCallback(() => setAnchorEl(null), []);

  const setLang = useCallback(
    (v: Lang) => {
      if (v !== lng) void i18n.changeLanguage(v);
      close();
    },
    [lng, i18n, close]
  );

  return (
    <>
      <Box
        component="button"
        type="button"
        id="language-switcher-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={t('common.language')}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 64,
          height: 40,
          px: 1.25,
          py: 0.5,
          boxSizing: 'border-box',
          borderRadius: 1,
          cursor: 'pointer',
          m: 0,
          color: inverted ? '#fff' : 'inherit',
          bgcolor: inverted ? 'transparent' : 'rgba(255,255,255,0.92)',
          border: inverted ? '1px solid rgba(255,255,255,0.5)' : '1px solid rgba(0,0,0,0.23)',
          '&:hover': {
            borderColor: inverted ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.4)',
          },
          '&:focus-visible': {
            outline: inverted ? '2px solid rgba(255,255,255,0.85)' : '2px solid rgba(25,118,210,0.6)',
            outlineOffset: 2,
          },
        }}
      >
        <Box
          aria-hidden
          sx={{
            fontSize: 20,
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {flagEmoji(lng)}
        </Box>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        marginThreshold={8}
        slotProps={{
          paper: {
            elevation: 6,
            sx: {
              borderRadius: 1,
              overflow: 'hidden',
              border: inverted ? '1px solid rgba(255,255,255,0.35)' : '1px solid rgba(0,0,0,0.12)',
              mt: 0.5,
              bgcolor: menuBg,
            },
          },
        }}
      >
        <Box
          id={listId}
          role="listbox"
          aria-labelledby="language-switcher-trigger"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            py: 1,
            px: 1,
            minWidth: 52,
            boxSizing: 'border-box',
          }}
        >
          {LANGS.map((code) => {
            const selected = code === lng;
            return (
              <Box
                key={code}
                component="button"
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => setLang(code)}
                aria-label={code === 'ru' ? t('language.ru') : t('language.en')}
                sx={{
                  width: 34,
                  height: 34,
                  minWidth: 34,
                  p: 0,
                  m: 0,
                  border: `2px solid ${selected ? flagBorder : flagBorderMuted}`,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1rem',
                  lineHeight: 1,
                  cursor: 'pointer',
                  overflow: 'hidden',
                  bgcolor: 'transparent',
                  opacity: selected ? 1 : inverted ? 0.55 : 0.65,
                  transition: 'opacity 0.15s ease, transform 0.15s ease',
                  '&:hover': {
                    opacity: 1,
                    transform: 'scale(1.06)',
                  },
                  '&:focus-visible': {
                    outline: inverted ? '2px solid rgba(255,255,255,0.85)' : '2px solid rgba(25,118,210,0.6)',
                    outlineOffset: 2,
                  },
                }}
              >
                {flagEmoji(code)}
              </Box>
            );
          })}
        </Box>
      </Popover>
    </>
  );
};

export default LanguageSwitcher;
